import type { ChatSession } from "@stockline/shared/types/ai/chat-session"
import { CHAT_NOTIFICATION_TYPE } from "@stockline/shared/types/ai/chat-session"
import type { ChatMessage } from "@stockline/shared/types/ai/chat-message"
import { useCallback } from "react"
import { CHAT_SESSION_TABLE } from "../../../lib/offline/resources/ai/chat-session/db"
import { CHAT_MESSAGE_TABLE } from "../../../lib/offline/resources/ai/chat-message/db"
import { readViewContextSnapshot } from "../../../lib/view-context/store"
import { isViewContextDebugEnabled } from "../../../lib/view-context/debug"
import { useAISync } from "../providers/AISyncProvider"

export function useChatMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createSession = useCallback(
    async (data?: {
      title?: string
      sourceExecutionId?: string
    }): Promise<ChatSession> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const session: ChatSession = {
        id,
        companyId: "",
        userId: "",
        title: data?.title ?? null,
        sourceExecutionId: data?.sourceExecutionId ?? null,
        // User-created chats are never about a specific dashboard; a source
        // dashboard is set only by the notify pipeline for BI briefs.
        sourceDashboardId: null,
        sourceDashboardIds: null,
        unread: false,
        // User-created chats are always ordinary; `bi` is set only by the
        // notify pipeline for Business-Intelligence briefs.
        notificationType: CHAT_NOTIFICATION_TYPE.GENERAL,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }

      await storage.put(CHAT_SESSION_TABLE, session)
      await changeQueue.enqueue("chat-sessions", {
        op: "create",
        id,
        data: session,
      })
      void engine.pushChanges("chat-sessions")

      return session
    },
    [storage, changeQueue, engine]
  )

  const deleteSession = useCallback(
    async (id: string) => {
      await storage.delete(CHAT_SESSION_TABLE, id)
      await changeQueue.enqueue("chat-sessions", { op: "delete", id })
      void engine.pushChanges("chat-sessions")
    },
    [storage, changeQueue, engine]
  )

  // Drain the pending session push queue and wait for it to reach the server.
  // `createSession` only fire-and-forgets its push, so anything that depends on
  // the session existing server-side immediately afterward can race ahead of the
  // session sync and fail with "Session not found". Two such followers exist:
  //   - the first message of a new conversation, whose `chat-messages` push can
  //     overtake the `chat-sessions` push (push-batch 500s) — see `sendMessage`;
  //   - a chat-media multipart upload, which hits the server DIRECTLY and 404s.
  // Both await this first (sessions-first). Safe online and offline: `pushChanges`
  // coalesces in-flight drains and resolves even when the push fails (the entry
  // stays queued and retries later).
  const flushSessions = useCallback(
    () => engine.pushChanges("chat-sessions"),
    [engine]
  )

  const sendMessage = useCallback(
    async (
      sessionId: string,
      content: string,
      mediaIds?: string[],
      allowedTools?: string[]
    ) => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      // Optimistic write — useLiveQuery will pick it up instantly
      const message: ChatMessage = {
        id,
        sessionId,
        companyId: "",
        executionId: null,
        role: "user",
        content,
        position: Date.now(), // Temporary position, server will assign real one
        status: 0,
        toolCalls: null,
        toolResults: null,
        proposalBatchId: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }

      // Snapshot what the user is currently looking at, so the AI gets
      // it as a system-prompt block on this turn. Captured at send time
      // (not at hook-render time) — guarantees freshness even if the
      // user scrolled or changed filters between renders.
      const viewContext = readViewContextSnapshot()
      if (isViewContextDebugEnabled()) {
        console.log(
          "[view_context] chat send →",
          viewContext
            ? `attached (page=${viewContext.page})`
            : "no snapshot, sending without"
        )
      }

      await storage.put(CHAT_MESSAGE_TABLE, message)
      await changeQueue.enqueue("chat-messages", {
        op: "create",
        id,
        data: { sessionId, role: "user", content },
        ...(mediaIds?.length ? { mediaIds } : {}),
        ...(allowedTools ? { allowedTools } : {}),
        ...(viewContext ? { viewContext } : {}),
      })

      // Sessions-first: drain the session push before the message push, or the
      // first message of a new conversation can reach the server before its
      // session and 500 with "Session not found". See `flushSessions`.
      await flushSessions()
      void engine.pushChanges("chat-messages")
    },
    [storage, changeQueue, engine, flushSessions]
  )

  const cancelExecution = useCallback(
    async (executionId: string) => {
      await engine.http.post(
        `/api/v2/chat-executions/${executionId}/cancel`,
        {}
      )
    },
    [engine]
  )

  const setSessionUnread = useCallback(
    async (sessionId: string, unread: boolean): Promise<void> => {
      const existing = await storage.get<ChatSession>(
        CHAT_SESSION_TABLE,
        sessionId
      )
      if (!existing || existing.unread === unread) return

      const now = new Date().toISOString()
      const patch = { unread, updatedAt: now }

      await storage.put(CHAT_SESSION_TABLE, { ...existing, ...patch })
      await changeQueue.enqueue<ChatSession>("chat-sessions", {
        op: "update",
        id: sessionId,
        data: patch,
      })
      void engine.pushChanges("chat-sessions")
    },
    [storage, changeQueue, engine]
  )

  const markSessionRead = useCallback(
    (sessionId: string): Promise<void> => setSessionUnread(sessionId, false),
    [setSessionUnread]
  )

  const markSessionUnread = useCallback(
    (sessionId: string): Promise<void> => setSessionUnread(sessionId, true),
    [setSessionUnread]
  )

  return {
    createSession,
    deleteSession,
    sendMessage,
    cancelExecution,
    flushSessions,
    markSessionRead,
    markSessionUnread,
  }
}
